// src/hooks/useDebouncedSearch.js
import { useEffect, useRef, useState } from "react";

/**
 * Debounced search with abort + reset.
 *
 * @param {Function} fetcher  (query, signal) => Promise<response>
 * @param {number}   delay    debounce delay in ms
 * @returns {{
 *   query: string,
 *   setQuery: Function,
 *   results: any[],
 *   setResults: Function,
 *   count: number,
 *   setCount: Function,
 *   next: string|null,
 *   loading: boolean,
 *   error: string|null,
 *   loadMore: Function,
 *   reset: Function,
 * }}
 */
export function useDebouncedSearch(fetcher, delay = 300) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debounceTimer = useRef(null);
  const abortRef = useRef(null);

  // Debounce + fire request on query change
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      // Cancel previous request if any
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      fetcher(query, controller.signal)
        .then((res) => {
          const data = res.data;
          const items = Array.isArray(data) ? data : data?.results ?? [];
          setResults(items);
          setCount(
            Array.isArray(data) ? items.length : data?.count ?? items.length
          );
          setNext(Array.isArray(data) ? null : data?.next ?? null);
        })
        .catch((err) => {
          // Ignore aborted requests
          if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
          setError(err.message || "Search failed.");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, delay);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query, delay, fetcher]);

  // Load the next page and append
  const loadMore = async () => {
    if (!next || loading) return;
    setLoading(true);
    try {
      // If `next` is a full URL, fetcher won't handle it — use the raw endpoint.
      // We keep the same response-shape handling as the main fetch.
      const res = await fetcher.__fetchUrl
        ? fetcher.__fetchUrl(next)
        : fetch(next).then((r) => r.json());
      const data = res.data ?? res;
      const items = Array.isArray(data) ? data : data?.results ?? [];
      setResults((prev) => [...prev, ...items]);
      setCount(
        Array.isArray(data) ? items.length : data?.count ?? items.length
      );
      setNext(data?.next ?? null);
    } catch (err) {
      setError(err.message || "Failed to load more.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (abortRef.current) abortRef.current.abort();
    setQuery("");
    setResults([]);
    setCount(0);
    setNext(null);
    setError(null);
  };

  return {
    query,
    setQuery,
    results,
    setResults,
    count,
    setCount,
    next,
    loading,
    error,
    loadMore,
    reset,
  };
}